
import { useState, useEffect } from 'react';
import { Message } from '@/types/chat';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export const useChat = (roomId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!roomId || !user) {
      setMessages([]);
      return;
    }

    const fetchMessages = async () => {
      try {
        setIsLoading(true);
        // Get all messages for the room
        const { data, error } = await supabase
          .from('chat_messages')
          .select(`
            id,
            room_id,
            content,
            created_at,
            sender_id,
            sender:profiles!sender_id(
              username,
              avatar_url
            )
          `)
          .eq('room_id', roomId)
          .order('created_at');

        if (error) {
          console.error('Error fetching messages:', error);
          return;
        }

        // Transform to match our Message type
        const transformedMessages: Message[] = data.map(msg => {
          let senderUsername: string | undefined;
          let senderAvatarUrl: string | undefined;
          
          // Handle the sender data which could be in different formats
          if (msg.sender) {
            if (Array.isArray(msg.sender) && msg.sender.length > 0) {
              // When sender is an array (from the join)
              senderUsername = msg.sender[0]?.username;
              senderAvatarUrl = msg.sender[0]?.avatar_url;
            } else if (typeof msg.sender === 'object') {
              // When sender is a single object
              senderUsername = (msg.sender as any).username;
              senderAvatarUrl = (msg.sender as any).avatar_url;
            }
          }

          return {
            id: msg.id,
            room_id: msg.room_id,
            content: msg.content,
            created_at: msg.created_at,
            sender_id: msg.sender_id,
            sender: {
              username: senderUsername,
              avatar_url: senderAvatarUrl
            }
          };
        });

        setMessages(transformedMessages);
      } catch (error) {
        console.error('Unexpected error in useChat:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();

    // Set up real-time subscription for new messages
    const messagesSubscription = supabase
      .channel(`room_${roomId}_messages`)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`
        }, 
        async (payload) => {
          // Get the sender info
          const { data: senderData, error: senderError } = await supabase
            .from('profiles')
            .select('username, avatar_url')
            .eq('id', payload.new.sender_id)
            .single();
          
          if (senderError) {
            console.error('Error fetching sender data:', senderError);
          }
          
          // Add the new message to the state with sender info
          const newMessage: Message = {
            id: payload.new.id,
            room_id: payload.new.room_id,
            sender_id: payload.new.sender_id,
            content: payload.new.content,
            created_at: payload.new.created_at,
            sender: {
              username: senderData?.username,
              avatar_url: senderData?.avatar_url
            }
          };
          
          setMessages(currentMessages => [...currentMessages, newMessage]);
        }
      )
      .subscribe();

    return () => {
      messagesSubscription.unsubscribe();
    };
  }, [roomId, user]);

  const sendMessage = async (content: string) => {
    if (!roomId || !user || !content.trim()) {
      return;
    }

    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          room_id: roomId,
          sender_id: user.id,
          content
        });

      if (error) {
        console.error('Error sending message:', error);
      }
    } catch (error) {
      console.error('Unexpected error in sendMessage:', error);
    }
  };

  return { messages, isLoading, sendMessage };
};
